/**
 * Comprehensive Financial Questionnaire
 * 
 * 7 Sections, 50+ Questions
 * Similar to what a professional financial advisor would ask
 */

import type { Question, QuestionnaireSection, QuestionSection } from '@/types'
import { SECTION_INFO } from '@/types/questionnaire.types'
import { INDUSTRIES } from '@/types/lifePlan.types'

// ============================================================================
// US STATES
// ============================================================================

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'Washington D.C.' },
]

// ============================================================================
// SECTION 1: PERSONAL INFORMATION
// ============================================================================

const personalQuestions: Question[] = [
  {
    id: 'personal_first_name',
    section: 'personal',
    order: 1,
    question: "What's your first name?",
    description: "We'll use this to personalize your experience",
    type: 'text',
    stateKey: 'personal.firstName',
    validation: { required: true },
    placeholder: 'Enter your first name',
    icon: 'User',
  },
  {
    id: 'personal_last_name',
    section: 'personal',
    order: 2,
    question: "What's your last name?",
    type: 'text',
    stateKey: 'personal.lastName',
    validation: { required: true },
    placeholder: 'Enter your last name',
  },
  {
    id: 'personal_birth_date',
    section: 'personal',
    order: 3,
    question: 'When were you born?',
    description: 'This helps us calculate projections based on your age',
    type: 'date',
    stateKey: 'personal.birthDate',
    validation: { required: true },
    icon: 'Calendar',
  },
  {
    id: 'personal_state',
    section: 'personal',
    order: 4,
    question: 'Which state do you live in?',
    description: 'State taxes vary significantly and affect your projections',
    type: 'state',
    stateKey: 'personal.state',
    options: US_STATES,
    validation: { required: true },
    icon: 'MapPin',
  },
  {
    id: 'personal_filing_status',
    section: 'personal',
    order: 5,
    question: 'What is your tax filing status?',
    type: 'select',
    stateKey: 'personal.filingStatus',
    options: [
      { value: 'single', label: 'Single' },
      { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
      { value: 'married_filing_separately', label: 'Married Filing Separately' },
      { value: 'head_of_household', label: 'Head of Household' },
    ],
    validation: { required: true },
    icon: 'FileText',
  },
  {
    id: 'personal_retirement_age',
    section: 'personal',
    order: 6,
    question: 'At what age do you plan to retire?',
    description: 'This is a key milestone for your financial plan',
    type: 'number',
    stateKey: 'personal.retirementAge',
    validation: { required: true, min: 40, max: 90 },
    defaultValue: 65,
    placeholder: '65',
    icon: 'Sunset',
  },
  {
    id: 'personal_life_expectancy',
    section: 'personal',
    order: 7,
    question: 'How long do you expect to live?',
    description: 'Used to ensure your money lasts. Most people underestimate this!',
    helpText: 'Consider your family history and health. The average is 85, but many live past 95.',
    type: 'number',
    stateKey: 'personal.lifeExpectancy',
    validation: { required: true, min: 70, max: 110 },
    defaultValue: 90,
    placeholder: '90',
    icon: 'Heart',
  },
]

// ============================================================================
// SECTION 2: FAMILY PLANNING
// ============================================================================

const familyQuestions: Question[] = [
  {
    id: 'family_marital_status',
    section: 'family',
    order: 1,
    question: 'What is your marital/relationship status?',
    type: 'select',
    stateKey: 'family.maritalStatus',
    options: [
      { value: 'single', label: 'Single' },
      { value: 'married', label: 'Married' },
      { value: 'partnered', label: 'Domestic Partnership' },
      { value: 'divorced', label: 'Divorced' },
      { value: 'widowed', label: 'Widowed' },
    ],
    validation: { required: true },
    icon: 'Heart',
  },
  {
    id: 'family_spouse_name',
    section: 'family',
    order: 2,
    question: "What is your spouse/partner's first name?",
    type: 'text',
    stateKey: 'family.spouse.firstName',
    showIf: [{ questionId: 'family_marital_status', operator: 'includes', value: 'married,partnered' }],
    placeholder: 'Enter their first name',
  },
  {
    id: 'family_spouse_birth_date',
    section: 'family',
    order: 3,
    question: "When was your spouse/partner born?",
    type: 'date',
    stateKey: 'family.spouse.birthDate',
    showIf: [{ questionId: 'family_marital_status', operator: 'includes', value: 'married,partnered' }],
  },
  {
    id: 'family_spouse_income',
    section: 'family',
    order: 4,
    question: "What is your spouse/partner's annual income?",
    type: 'currency',
    stateKey: 'family.spouse.currentIncome',
    showIf: [{ questionId: 'family_marital_status', operator: 'includes', value: 'married,partnered' }],
    placeholder: '0',
    helpText: 'Enter 0 if not working',
  },
  {
    id: 'family_spouse_retirement_age',
    section: 'family',
    order: 5,
    question: 'At what age does your spouse/partner plan to retire?',
    type: 'number',
    stateKey: 'family.spouse.retirementAge',
    showIf: [{ questionId: 'family_marital_status', operator: 'includes', value: 'married,partnered' }],
    validation: { min: 40, max: 90 },
    defaultValue: 65,
  },
  {
    id: 'family_has_children',
    section: 'family',
    order: 6,
    question: 'Do you have any children?',
    type: 'radio',
    stateKey: 'family.hasChildren',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Users',
  },
  {
    id: 'family_num_children',
    section: 'family',
    order: 7,
    question: 'How many children do you have?',
    type: 'number',
    stateKey: 'family.numChildren',
    showIf: [{ questionId: 'family_has_children', operator: 'equals', value: 'yes' }],
    validation: { min: 1, max: 20 },
  },
  {
    id: 'family_children_ages',
    section: 'family',
    order: 8,
    question: "What are your children's ages?",
    description: 'Enter ages separated by commas (e.g., 5, 8, 12)',
    type: 'text',
    stateKey: 'family.childrenAges',
    showIf: [{ questionId: 'family_has_children', operator: 'equals', value: 'yes' }],
    placeholder: 'e.g., 5, 8, 12',
  },
  {
    id: 'family_college_funding',
    section: 'family',
    order: 9,
    question: 'Do you plan to help pay for college?',
    type: 'select',
    stateKey: 'family.collegeFundingPlan',
    showIf: [{ questionId: 'family_has_children', operator: 'equals', value: 'yes' }],
    options: [
      { value: 'full', label: 'Yes, I plan to pay 100%' },
      { value: 'partial', label: 'Yes, I plan to pay some' },
      { value: 'none', label: 'No, they will pay for their own' },
    ],
  },
  {
    id: 'family_planning_children',
    section: 'family',
    order: 10,
    question: 'Are you planning to have (more) children?',
    type: 'radio',
    stateKey: 'family.planningChildren',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'maybe', label: 'Maybe' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Baby',
  },
  {
    id: 'family_planned_children_count',
    section: 'family',
    order: 11,
    question: 'How many more children are you planning?',
    type: 'number',
    stateKey: 'family.plannedChildrenCount',
    showIf: [{ questionId: 'family_planning_children', operator: 'includes', value: 'yes,maybe' }],
    validation: { min: 1, max: 10 },
  },
  {
    id: 'family_first_child_year',
    section: 'family',
    order: 12,
    question: 'What year do you expect your next child?',
    type: 'year',
    stateKey: 'family.firstPlannedChildYear',
    showIf: [{ questionId: 'family_planning_children', operator: 'includes', value: 'yes,maybe' }],
  },
  {
    id: 'family_has_dependents',
    section: 'family',
    order: 13,
    question: 'Do you financially support any other dependents?',
    description: 'Parents, siblings, or others who depend on you',
    type: 'radio',
    stateKey: 'family.hasDependents',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    id: 'family_dependents_monthly',
    section: 'family',
    order: 14,
    question: 'How much do you spend monthly supporting dependents?',
    type: 'currency',
    stateKey: 'family.dependentsMonthly',
    showIf: [{ questionId: 'family_has_dependents', operator: 'equals', value: 'yes' }],
    placeholder: '500',
  },
]

// ============================================================================
// SECTION 3: CAREER & INCOME
// ============================================================================

const careerQuestions: Question[] = [
  {
    id: 'career_employment_status',
    section: 'career',
    order: 1,
    question: 'What is your current employment status?',
    type: 'select',
    stateKey: 'career.employmentStatus',
    options: [
      { value: 'employed', label: 'Employed (W-2)' },
      { value: 'self-employed', label: 'Self-Employed / Business Owner' },
      { value: 'unemployed', label: 'Unemployed' },
      { value: 'student', label: 'Student' },
      { value: 'retired', label: 'Retired' },
    ],
    validation: { required: true },
    icon: 'Briefcase',
  },
  {
    id: 'career_industry',
    section: 'career',
    order: 2,
    question: 'What industry do you work in?',
    description: 'We use this to predict wage growth in your field',
    type: 'select',
    stateKey: 'career.currentJob.industry',
    options: INDUSTRIES.map(ind => ({ value: ind, label: ind })),
    showIf: [{ questionId: 'career_employment_status', operator: 'includes', value: 'employed,self-employed' }],
    validation: { required: true },
    icon: 'Building',
  },
  {
    id: 'career_job_title',
    section: 'career',
    order: 3,
    question: 'What is your job title?',
    type: 'text',
    stateKey: 'career.currentJob.title',
    showIf: [{ questionId: 'career_employment_status', operator: 'includes', value: 'employed,self-employed' }],
    placeholder: 'e.g., Software Engineer, Marketing Manager',
  },
  {
    id: 'career_employer',
    section: 'career',
    order: 4,
    question: 'Who is your employer?',
    type: 'text',
    stateKey: 'career.currentJob.employer',
    showIf: [{ questionId: 'career_employment_status', operator: 'equals', value: 'employed' }],
    placeholder: 'Company name',
    allowSkip: true,
  },
  {
    id: 'career_years_experience',
    section: 'career',
    order: 5,
    question: 'How many years of experience do you have in your field?',
    type: 'number',
    stateKey: 'career.currentJob.yearsExperience',
    showIf: [{ questionId: 'career_employment_status', operator: 'includes', value: 'employed,self-employed' }],
    validation: { min: 0, max: 60 },
    placeholder: '5',
  },
  {
    id: 'career_education',
    section: 'career',
    order: 6,
    question: 'What is your highest level of education?',
    type: 'select',
    stateKey: 'career.educationLevel',
    options: [
      { value: 'high_school', label: 'High School / GED' },
      { value: 'some_college', label: 'Some College' },
      { value: 'associates', label: "Associate's Degree" },
      { value: 'bachelors', label: "Bachelor's Degree" },
      { value: 'masters', label: "Master's Degree" },
      { value: 'doctorate', label: 'Doctorate (PhD)' },
      { value: 'professional', label: 'Professional Degree (MD, JD, etc.)' },
    ],
    icon: 'GraduationCap',
  },
  {
    id: 'career_base_salary',
    section: 'career',
    order: 7,
    question: 'What is your annual base salary?',
    description: 'Your regular salary before bonuses and commissions',
    type: 'currency',
    stateKey: 'career.currentJob.baseSalary',
    showIf: [{ questionId: 'career_employment_status', operator: 'includes', value: 'employed,self-employed' }],
    validation: { required: true },
    placeholder: '75000',
    icon: 'DollarSign',
  },
  {
    id: 'career_bonus',
    section: 'career',
    order: 8,
    question: 'What is your expected annual bonus?',
    type: 'currency',
    stateKey: 'career.currentJob.bonus',
    showIf: [{ questionId: 'career_employment_status', operator: 'equals', value: 'employed' }],
    placeholder: '0',
    defaultValue: 0,
  },
  {
    id: 'career_commission',
    section: 'career',
    order: 9,
    question: 'What is your expected annual commission?',
    type: 'currency',
    stateKey: 'career.currentJob.commission',
    showIf: [{ questionId: 'career_employment_status', operator: 'equals', value: 'employed' }],
    placeholder: '0',
    defaultValue: 0,
  },
  {
    id: 'career_stock_comp',
    section: 'career',
    order: 10,
    question: 'What is your annual stock compensation value?',
    description: 'RSUs, options, or other equity compensation',
    type: 'currency',
    stateKey: 'career.currentJob.stockCompensation',
    showIf: [{ questionId: 'career_employment_status', operator: 'equals', value: 'employed' }],
    placeholder: '0',
    defaultValue: 0,
  },
  {
    id: 'career_401k_match',
    section: 'career',
    order: 11,
    question: 'Does your employer match 401(k) contributions?',
    type: 'radio',
    stateKey: 'career.has401kMatch',
    showIf: [{ questionId: 'career_employment_status', operator: 'equals', value: 'employed' }],
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    id: 'career_401k_match_percent',
    section: 'career',
    order: 12,
    question: 'What percentage does your employer match?',
    description: 'e.g., 50% of your contribution up to 6% of salary',
    type: 'percentage',
    stateKey: 'career.currentJob.employerMatch401k',
    showIf: [{ questionId: 'career_401k_match', operator: 'equals', value: 'yes' }],
    validation: { min: 0, max: 100 },
    placeholder: '50',
  },
  {
    id: 'career_side_income',
    section: 'career',
    order: 13,
    question: 'Do you have any side income?',
    description: 'Freelance work, rental income, gig economy, etc.',
    type: 'radio',
    stateKey: 'career.hasSideIncome',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Coins',
  },
  {
    id: 'career_side_income_amount',
    section: 'career',
    order: 14,
    question: 'How much annual side income do you earn?',
    type: 'currency',
    stateKey: 'career.sideIncomeTotal',
    showIf: [{ questionId: 'career_side_income', operator: 'equals', value: 'yes' }],
    placeholder: '10000',
  },
  {
    id: 'career_change_planned',
    section: 'career',
    order: 15,
    question: 'Are you planning any major career changes?',
    description: 'Job change, promotion, sabbatical, industry switch',
    type: 'radio',
    stateKey: 'career.hasPlannedChanges',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
]

// ============================================================================
// SECTION 4: CURRENT FINANCES
// ============================================================================

const financesQuestions: Question[] = [
  {
    id: 'finances_checking',
    section: 'finances',
    order: 1,
    question: 'How much do you have in checking accounts?',
    type: 'currency',
    stateKey: 'finances.checking',
    placeholder: '5000',
    icon: 'Wallet',
  },
  {
    id: 'finances_savings',
    section: 'finances',
    order: 2,
    question: 'How much do you have in savings accounts?',
    type: 'currency',
    stateKey: 'finances.savings',
    placeholder: '10000',
    icon: 'PiggyBank',
  },
  {
    id: 'finances_emergency_fund',
    section: 'finances',
    order: 3,
    question: 'Do you have a dedicated emergency fund?',
    type: 'radio',
    stateKey: 'finances.hasEmergencyFund',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    helpText: 'Experts recommend 3-6 months of expenses',
    icon: 'ShieldCheck',
  },
  {
    id: 'finances_emergency_amount',
    section: 'finances',
    order: 4,
    question: 'How much is in your emergency fund?',
    type: 'currency',
    stateKey: 'finances.emergencyFundAmount',
    showIf: [{ questionId: 'finances_emergency_fund', operator: 'equals', value: 'yes' }],
    placeholder: '15000',
  },
  {
    id: 'finances_has_investments',
    section: 'finances',
    order: 5,
    question: 'Do you have any taxable investment accounts?',
    description: 'Brokerage accounts, stocks, bonds, ETFs (not retirement)',
    type: 'radio',
    stateKey: 'finances.hasInvestments',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'TrendingUp',
  },
  {
    id: 'finances_investment_value',
    section: 'finances',
    order: 6,
    question: 'What is the total value of your taxable investments?',
    type: 'currency',
    stateKey: 'finances.investmentValue',
    showIf: [{ questionId: 'finances_has_investments', operator: 'equals', value: 'yes' }],
    placeholder: '50000',
  },
  {
    id: 'finances_has_401k',
    section: 'finances',
    order: 7,
    question: 'Do you have a 401(k) or 403(b)?',
    type: 'radio',
    stateKey: 'finances.has401k',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Briefcase',
  },
  {
    id: 'finances_401k_balance',
    section: 'finances',
    order: 8,
    question: 'What is your 401(k)/403(b) balance?',
    type: 'currency',
    stateKey: 'finances.balance401k',
    showIf: [{ questionId: 'finances_has_401k', operator: 'equals', value: 'yes' }],
    placeholder: '75000',
  },
  {
    id: 'finances_401k_contribution',
    section: 'finances',
    order: 9,
    question: 'What percentage of your salary do you contribute to 401(k)?',
    type: 'percentage',
    stateKey: 'finances.contribution401k',
    showIf: [{ questionId: 'finances_has_401k', operator: 'equals', value: 'yes' }],
    validation: { min: 0, max: 100 },
    placeholder: '10',
    helpText: '2025 limit is $23,500 ($31,000 if 50+)',
  },
  {
    id: 'finances_has_ira',
    section: 'finances',
    order: 10,
    question: 'Do you have an IRA?',
    type: 'select',
    stateKey: 'finances.iraType',
    options: [
      { value: 'none', label: 'No IRA' },
      { value: 'traditional', label: 'Traditional IRA' },
      { value: 'roth', label: 'Roth IRA' },
      { value: 'both', label: 'Both Traditional and Roth' },
    ],
    icon: 'Receipt',
  },
  {
    id: 'finances_ira_balance',
    section: 'finances',
    order: 11,
    question: 'What is your total IRA balance?',
    type: 'currency',
    stateKey: 'finances.iraBalance',
    showIf: [{ questionId: 'finances_has_ira', operator: 'not_equals', value: 'none' }],
    placeholder: '25000',
  },
  {
    id: 'finances_has_hsa',
    section: 'finances',
    order: 12,
    question: 'Do you have a Health Savings Account (HSA)?',
    type: 'radio',
    stateKey: 'finances.hasHSA',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Heart',
  },
  {
    id: 'finances_hsa_balance',
    section: 'finances',
    order: 13,
    question: 'What is your HSA balance?',
    type: 'currency',
    stateKey: 'finances.hsaBalance',
    showIf: [{ questionId: 'finances_has_hsa', operator: 'equals', value: 'yes' }],
    placeholder: '5000',
  },
  {
    id: 'finances_has_mortgage',
    section: 'finances',
    order: 14,
    question: 'Do you own a home with a mortgage?',
    type: 'radio',
    stateKey: 'finances.hasMortgage',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Home',
  },
  {
    id: 'finances_home_value',
    section: 'finances',
    order: 15,
    question: 'What is your home worth?',
    type: 'currency',
    stateKey: 'finances.homeValue',
    showIf: [{ questionId: 'finances_has_mortgage', operator: 'equals', value: 'yes' }],
    placeholder: '350000',
  },
  {
    id: 'finances_mortgage_balance',
    section: 'finances',
    order: 16,
    question: 'What is your remaining mortgage balance?',
    type: 'currency',
    stateKey: 'finances.mortgageBalance',
    showIf: [{ questionId: 'finances_has_mortgage', operator: 'equals', value: 'yes' }],
    placeholder: '280000',
  },
  {
    id: 'finances_mortgage_rate',
    section: 'finances',
    order: 17,
    question: 'What is your mortgage interest rate?',
    type: 'percentage',
    stateKey: 'finances.mortgageRate',
    showIf: [{ questionId: 'finances_has_mortgage', operator: 'equals', value: 'yes' }],
    validation: { min: 0, max: 15 },
    placeholder: '6.5',
  },
  {
    id: 'finances_has_student_loans',
    section: 'finances',
    order: 18,
    question: 'Do you have student loans?',
    type: 'radio',
    stateKey: 'finances.hasStudentLoans',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'GraduationCap',
  },
  {
    id: 'finances_student_loan_balance',
    section: 'finances',
    order: 19,
    question: 'What is your total student loan balance?',
    type: 'currency',
    stateKey: 'finances.studentLoanBalance',
    showIf: [{ questionId: 'finances_has_student_loans', operator: 'equals', value: 'yes' }],
    placeholder: '45000',
  },
  {
    id: 'finances_has_credit_card_debt',
    section: 'finances',
    order: 20,
    question: 'Do you carry credit card balances?',
    type: 'radio',
    stateKey: 'finances.hasCreditCardDebt',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No, I pay in full each month' },
    ],
    icon: 'CreditCard',
  },
  {
    id: 'finances_credit_card_balance',
    section: 'finances',
    order: 21,
    question: 'What is your total credit card balance?',
    type: 'currency',
    stateKey: 'finances.creditCardBalance',
    showIf: [{ questionId: 'finances_has_credit_card_debt', operator: 'equals', value: 'yes' }],
    placeholder: '5000',
  },
  {
    id: 'finances_has_other_debt',
    section: 'finances',
    order: 22,
    question: 'Do you have any other debts?',
    description: 'Auto loans, personal loans, medical debt, etc.',
    type: 'radio',
    stateKey: 'finances.hasOtherDebt',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    id: 'finances_other_debt_balance',
    section: 'finances',
    order: 23,
    question: 'What is your total other debt balance?',
    type: 'currency',
    stateKey: 'finances.otherDebtBalance',
    showIf: [{ questionId: 'finances_has_other_debt', operator: 'equals', value: 'yes' }],
    placeholder: '15000',
  },
]

// ============================================================================
// SECTION 5: INSURANCE
// ============================================================================

const insuranceQuestions: Question[] = [
  {
    id: 'insurance_health_type',
    section: 'insurance',
    order: 1,
    question: 'What type of health insurance do you have?',
    type: 'select',
    stateKey: 'insurance.health.type',
    options: [
      { value: 'employer', label: 'Employer-Provided' },
      { value: 'marketplace', label: 'ACA Marketplace' },
      { value: 'medicare', label: 'Medicare' },
      { value: 'medicaid', label: 'Medicaid' },
      { value: 'none', label: 'No Health Insurance' },
    ],
    validation: { required: true },
    icon: 'Heart',
  },
  {
    id: 'insurance_health_premium',
    section: 'insurance',
    order: 2,
    question: 'What is your monthly health insurance premium?',
    description: 'Your out-of-pocket cost (after any employer contribution)',
    type: 'currency',
    stateKey: 'insurance.health.monthlyPremium',
    showIf: [{ questionId: 'insurance_health_type', operator: 'not_equals', value: 'none' }],
    placeholder: '400',
  },
  {
    id: 'insurance_health_deductible',
    section: 'insurance',
    order: 3,
    question: 'What is your annual deductible?',
    type: 'currency',
    stateKey: 'insurance.health.deductible',
    showIf: [{ questionId: 'insurance_health_type', operator: 'not_equals', value: 'none' }],
    placeholder: '2000',
  },
  {
    id: 'insurance_has_life',
    section: 'insurance',
    order: 4,
    question: 'Do you have life insurance?',
    type: 'radio',
    stateKey: 'insurance.hasLifeInsurance',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Shield',
    helpText: 'Generally recommended if you have dependents',
  },
  {
    id: 'insurance_life_type',
    section: 'insurance',
    order: 5,
    question: 'What type of life insurance do you have?',
    type: 'select',
    stateKey: 'insurance.lifeType',
    showIf: [{ questionId: 'insurance_has_life', operator: 'equals', value: 'yes' }],
    options: [
      { value: 'term', label: 'Term Life' },
      { value: 'whole', label: 'Whole Life' },
      { value: 'universal', label: 'Universal Life' },
    ],
  },
  {
    id: 'insurance_life_coverage',
    section: 'insurance',
    order: 6,
    question: 'What is your life insurance coverage amount?',
    type: 'currency',
    stateKey: 'insurance.lifeCoverage',
    showIf: [{ questionId: 'insurance_has_life', operator: 'equals', value: 'yes' }],
    placeholder: '500000',
    helpText: 'Rule of thumb: 10-12x annual income',
  },
  {
    id: 'insurance_life_premium',
    section: 'insurance',
    order: 7,
    question: 'What is your monthly life insurance premium?',
    type: 'currency',
    stateKey: 'insurance.lifePremium',
    showIf: [{ questionId: 'insurance_has_life', operator: 'equals', value: 'yes' }],
    placeholder: '50',
  },
  {
    id: 'insurance_has_disability',
    section: 'insurance',
    order: 8,
    question: 'Do you have disability insurance?',
    type: 'radio',
    stateKey: 'insurance.hasDisability',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Umbrella',
    helpText: 'Protects your income if you become unable to work',
  },
  {
    id: 'insurance_disability_short_term',
    section: 'insurance',
    order: 9,
    question: 'Do you have short-term disability coverage?',
    type: 'radio',
    stateKey: 'insurance.disability.hasShortTerm',
    showIf: [{ questionId: 'insurance_has_disability', operator: 'equals', value: 'yes' }],
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    id: 'insurance_disability_long_term',
    section: 'insurance',
    order: 10,
    question: 'Do you have long-term disability coverage?',
    type: 'radio',
    stateKey: 'insurance.disability.hasLongTerm',
    showIf: [{ questionId: 'insurance_has_disability', operator: 'equals', value: 'yes' }],
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
  },
  {
    id: 'insurance_disability_coverage',
    section: 'insurance',
    order: 11,
    question: 'What percentage of income does it cover?',
    type: 'percentage',
    stateKey: 'insurance.disability.longTermCoverage',
    showIf: [{ questionId: 'insurance_disability_long_term', operator: 'equals', value: 'yes' }],
    validation: { min: 0, max: 100 },
    placeholder: '60',
    helpText: 'Most policies cover 50-70% of income',
  },
  {
    id: 'insurance_auto_annual',
    section: 'insurance',
    order: 12,
    question: 'How much do you pay annually for auto insurance?',
    type: 'currency',
    stateKey: 'insurance.autoAnnual',
    placeholder: '1200',
    icon: 'Car',
  },
  {
    id: 'insurance_home_annual',
    section: 'insurance',
    order: 13,
    question: 'How much do you pay annually for home/renters insurance?',
    type: 'currency',
    stateKey: 'insurance.homeAnnual',
    placeholder: '1500',
    icon: 'Home',
  },
  {
    id: 'insurance_has_umbrella',
    section: 'insurance',
    order: 14,
    question: 'Do you have umbrella liability insurance?',
    type: 'radio',
    stateKey: 'insurance.hasUmbrella',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    helpText: 'Extra liability protection beyond other policies',
  },
]

// ============================================================================
// SECTION 6: GOALS
// ============================================================================

const goalsQuestions: Question[] = [
  {
    id: 'goals_top_priority',
    section: 'goals',
    order: 1,
    question: 'What is your #1 financial priority right now?',
    type: 'select',
    stateKey: 'goals.topPriority',
    options: [
      { value: 'emergency_fund', label: 'Build emergency fund' },
      { value: 'debt_payoff', label: 'Pay off debt' },
      { value: 'home_purchase', label: 'Buy a home' },
      { value: 'retirement', label: 'Save for retirement' },
      { value: 'education', label: 'Save for education' },
      { value: 'investment', label: 'Grow investments' },
      { value: 'other', label: 'Other' },
    ],
    validation: { required: true },
    icon: 'Target',
  },
  {
    id: 'goals_buying_home',
    section: 'goals',
    order: 2,
    question: 'Are you planning to buy a home?',
    type: 'radio',
    stateKey: 'goals.planningHomePurchase',
    options: [
      { value: 'yes', label: 'Yes, in the next 5 years' },
      { value: 'maybe', label: 'Maybe someday' },
      { value: 'no', label: 'No / Already own' },
    ],
    icon: 'Home',
  },
  {
    id: 'goals_home_budget',
    section: 'goals',
    order: 3,
    question: 'What is your target home price?',
    type: 'currency',
    stateKey: 'goals.homeBudget',
    showIf: [{ questionId: 'goals_buying_home', operator: 'equals', value: 'yes' }],
    placeholder: '400000',
  },
  {
    id: 'goals_home_year',
    section: 'goals',
    order: 4,
    question: 'What year do you want to buy?',
    type: 'year',
    stateKey: 'goals.homeTargetYear',
    showIf: [{ questionId: 'goals_buying_home', operator: 'equals', value: 'yes' }],
  },
  {
    id: 'goals_big_purchase',
    section: 'goals',
    order: 5,
    question: 'Are you saving for any major purchases?',
    description: 'Car, vacation, wedding, etc.',
    type: 'radio',
    stateKey: 'goals.hasBigPurchase',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'ShoppingCart',
  },
  {
    id: 'goals_big_purchase_amount',
    section: 'goals',
    order: 6,
    question: 'How much do you need to save?',
    type: 'currency',
    stateKey: 'goals.bigPurchaseAmount',
    showIf: [{ questionId: 'goals_big_purchase', operator: 'equals', value: 'yes' }],
    placeholder: '30000',
  },
  {
    id: 'goals_charitable',
    section: 'goals',
    order: 7,
    question: 'Do you plan to give to charity?',
    type: 'select',
    stateKey: 'goals.charitableGiving',
    options: [
      { value: 'none', label: 'Not planning to' },
      { value: 'occasional', label: 'Occasional donations' },
      { value: 'regular', label: 'Regular giving (monthly/yearly)' },
      { value: 'legacy', label: 'Major legacy gift planned' },
    ],
    icon: 'Heart',
  },
  {
    id: 'goals_charitable_annual',
    section: 'goals',
    order: 8,
    question: 'How much do you give annually?',
    type: 'currency',
    stateKey: 'goals.charitableAnnual',
    showIf: [{ questionId: 'goals_charitable', operator: 'includes', value: 'regular,legacy' }],
    placeholder: '2000',
  },
  {
    id: 'goals_early_retirement',
    section: 'goals',
    order: 9,
    question: 'Are you interested in early retirement (FIRE)?',
    type: 'radio',
    stateKey: 'goals.interestedInFIRE',
    options: [
      { value: 'yes', label: 'Yes, that would be amazing' },
      { value: 'maybe', label: "Maybe, I'm curious" },
      { value: 'no', label: 'No, I like working' },
    ],
    icon: 'Flame',
  },
  {
    id: 'goals_leave_inheritance',
    section: 'goals',
    order: 10,
    question: 'Do you want to leave an inheritance?',
    type: 'select',
    stateKey: 'goals.leaveInheritance',
    options: [
      { value: 'no', label: 'No, I plan to spend it all' },
      { value: 'some', label: 'Yes, whatever is left' },
      { value: 'specific', label: 'Yes, a specific amount' },
    ],
    icon: 'Gift',
  },
  {
    id: 'goals_inheritance_amount',
    section: 'goals',
    order: 11,
    question: 'How much do you want to leave?',
    type: 'currency',
    stateKey: 'goals.inheritanceTarget',
    showIf: [{ questionId: 'goals_leave_inheritance', operator: 'equals', value: 'specific' }],
    placeholder: '500000',
  },
]

// ============================================================================
// SECTION 7: PREFERENCES
// ============================================================================

const preferencesQuestions: Question[] = [
  {
    id: 'preferences_risk_tolerance',
    section: 'preferences',
    order: 1,
    question: 'How would you describe your risk tolerance?',
    description: 'How do you feel about investment volatility?',
    type: 'select',
    stateKey: 'preferences.riskTolerance',
    options: [
      { value: 'conservative', label: 'Conservative - I prefer stability over growth' },
      { value: 'moderate', label: 'Moderate - Balanced approach' },
      { value: 'aggressive', label: 'Aggressive - I can handle big swings for higher returns' },
    ],
    validation: { required: true },
    icon: 'Scale',
  },
  {
    id: 'preferences_social_security_age',
    section: 'preferences',
    order: 2,
    question: 'When do you plan to start Social Security?',
    description: 'You can claim between 62 and 70. Later = higher monthly benefit.',
    type: 'number',
    stateKey: 'preferences.socialSecurityAge',
    validation: { min: 62, max: 70 },
    defaultValue: 67,
    helpText: 'Full retirement age is 67 for most. Waiting until 70 increases benefits by 24%.',
    icon: 'Landmark',
  },
  {
    id: 'preferences_social_security_estimate',
    section: 'preferences',
    order: 3,
    question: 'What is your expected monthly Social Security benefit?',
    description: 'Check ssa.gov/myaccount for your estimate',
    type: 'currency',
    stateKey: 'preferences.expectedSocialSecurity',
    placeholder: '2500',
    helpText: 'Average is about $1,900/month. Max is about $4,500.',
  },
  {
    id: 'preferences_has_pension',
    section: 'preferences',
    order: 4,
    question: 'Will you receive a pension?',
    type: 'radio',
    stateKey: 'preferences.hasPension',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Building2',
  },
  {
    id: 'preferences_pension_monthly',
    section: 'preferences',
    order: 5,
    question: 'What will your monthly pension be?',
    type: 'currency',
    stateKey: 'preferences.pensionMonthly',
    showIf: [{ questionId: 'preferences_has_pension', operator: 'equals', value: 'yes' }],
    placeholder: '2000',
  },
  {
    id: 'preferences_pension_age',
    section: 'preferences',
    order: 6,
    question: 'At what age will your pension start?',
    type: 'number',
    stateKey: 'preferences.pensionStartAge',
    showIf: [{ questionId: 'preferences_has_pension', operator: 'equals', value: 'yes' }],
    validation: { min: 50, max: 80 },
    defaultValue: 65,
  },
  {
    id: 'preferences_expecting_inheritance',
    section: 'preferences',
    order: 7,
    question: 'Are you expecting any inheritance?',
    type: 'radio',
    stateKey: 'preferences.expectingInheritance',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'maybe', label: 'Possibly' },
      { value: 'no', label: 'No' },
    ],
    icon: 'Gift',
  },
  {
    id: 'preferences_inheritance_amount',
    section: 'preferences',
    order: 8,
    question: 'Approximately how much do you expect to inherit?',
    type: 'currency',
    stateKey: 'preferences.expectedInheritance',
    showIf: [{ questionId: 'preferences_expecting_inheritance', operator: 'includes', value: 'yes,maybe' }],
    placeholder: '100000',
  },
  {
    id: 'preferences_inheritance_year',
    section: 'preferences',
    order: 9,
    question: 'What year do you expect to receive it?',
    type: 'year',
    stateKey: 'preferences.inheritanceYear',
    showIf: [{ questionId: 'preferences_expecting_inheritance', operator: 'includes', value: 'yes,maybe' }],
    helpText: 'Your best estimate',
  },
  {
    id: 'preferences_inflation_rate',
    section: 'preferences',
    order: 10,
    question: 'What inflation rate should we assume?',
    description: 'Historical average is about 2.5%',
    type: 'percentage',
    stateKey: 'preferences.inflationRate',
    validation: { min: 0, max: 10 },
    defaultValue: 2.5,
    icon: 'TrendingUp',
  },
]

// ============================================================================
// BUILD SECTIONS
// ============================================================================

function buildSection(id: QuestionSection, questions: Question[]): QuestionnaireSection {
  const info = SECTION_INFO[id]
  return {
    id,
    title: info.title,
    description: info.description,
    icon: info.icon,
    estimatedMinutes: info.estimatedMinutes,
    questions: questions.sort((a, b) => a.order - b.order),
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export const questionnaireSections: QuestionnaireSection[] = [
  buildSection('personal', personalQuestions),
  buildSection('family', familyQuestions),
  buildSection('career', careerQuestions),
  buildSection('finances', financesQuestions),
  buildSection('insurance', insuranceQuestions),
  buildSection('goals', goalsQuestions),
  buildSection('preferences', preferencesQuestions),
]

export const allQuestions: Question[] = [
  ...personalQuestions,
  ...familyQuestions,
  ...careerQuestions,
  ...financesQuestions,
  ...insuranceQuestions,
  ...goalsQuestions,
  ...preferencesQuestions,
]

export const totalQuestionCount = allQuestions.length

export const estimatedTotalMinutes = questionnaireSections.reduce(
  (sum, section) => sum + section.estimatedMinutes,
  0
)
